import { converterDataHoraBr } from "_utils/Data";
import { PrimaryButton } from "components/Button/style";
import { LoadingScreen } from "components/LoadingScreen";
import { useAbrirProcesso } from "features/core/pages/Financeiro/Pages/PagamentoTaxa/CriarLote/hooks/useAbrirProcesso";
import { useEffect, useState } from "react";
import { BotaoMoverTipo } from "../../../SolicitacaoPlaca/components/BotaoMoverTipo";
import {
  ModalRetorno,
  useModalRetorno,
} from "../../../SolicitacaoPlaca/components/ModalRetorno";
import {
  IColunaTabelaPadrao,
  TabelaPadrao,
} from "../../../SolicitacaoPlaca/components/TabelaPadrao";
import { LinkProcesso } from "../../../SolicitacaoPlaca/containers/Listar/style";
import { TIPO_SOLICITACAO_PLACA } from "../../../SolicitacaoPlaca/constants/tipoSolicitacaoPlaca";
import { DirecaoOrdenacao } from "../../../SolicitacaoPlaca/interfaces/IOrdenacao";
import { ORDENACAO_SOLICITACAO_TERCEIROS_PADRAO } from "../../constants/filtrosSolicitacaoTerceiros";
import { ResultadoEnvioTerceiros } from "../../hooks/useSolicitacoesTerceiros";
import { IDadosEnvioTerceiros } from "../../interfaces/IFornecedorTerceiros";
import { ISolicitacaoTerceiros } from "../../interfaces/ISolicitacaoTerceiros";
import { EnviarSolicitacaoModal } from "../../modals/EnviarSolicitacaoModal";

const SEM_VALOR = "—";

interface IProps {
  ativa: boolean;
  solicitacoes: ISolicitacaoTerceiros[];
  totalRegistros: number;
  carregando: boolean;
  limite: number;
  reiniciarPaginacao: boolean;
  onMudarPagina: (pagina: number) => void;
  onMudarLimite: (limite: number) => void;
  onOrdenar: (campo: string, direcao: DirecaoOrdenacao) => void;
  onExportar: () => void;
  exportando: boolean;
  onEnviar: (
    idsSolicitacao: number[],
    dados: IDadosEnvioTerceiros
  ) => Promise<ResultadoEnvioTerceiros>;
  enviando: boolean;
  onLoteEnviado: () => void;
  onMovidas: () => void;
}

export function Listar({
  ativa,
  solicitacoes,
  totalRegistros,
  carregando,
  limite,
  reiniciarPaginacao,
  onMudarPagina,
  onMudarLimite,
  onOrdenar,
  onExportar,
  exportando,
  onEnviar,
  enviando,
  onLoteEnviado,
  onMovidas,
}: IProps) {
  const [selecionados, setSelecionados] = useState<ISolicitacaoTerceiros[]>([]);
  const [mostrarEnvio, setMostrarEnvio] = useState(false);
  const [limparSelecao, setLimparSelecao] = useState(0);
  const { redirecionarProcesso } = useAbrirProcesso();
  const { mostrarRetorno, propsModalRetorno } = useModalRetorno();

  useEffect(() => {
    if (!ativa) setLimparSelecao((valor) => valor + 1);
  }, [ativa]);

  async function confirmarEnvio(dados: IDadosEnvioTerceiros) {
    const resultado = await onEnviar(
      selecionados.map(({ id }) => id),
      dados
    );

    if (!resultado.ok && !resultado.conflito) return;

    setMostrarEnvio(false);
    setLimparSelecao((valor) => valor + 1);
    onLoteEnviado();
    if (resultado.ok) {
      mostrarRetorno("Enviar solicitação", "Solicitação enviada com sucesso!");
    }
  }

  const colunas: IColunaTabelaPadrao<ISolicitacaoTerceiros>[] = [
    {
      chave: "dataSolicitacao",
      titulo: "Solicitação",
      selector: ({ dataSolicitacao }) =>
        converterDataHoraBr(dataSolicitacao) ?? SEM_VALOR,
      sortable: true,
      sortField: "dataSolicitacao",
      width: "190px",
    },
    {
      chave: "pdi",
      titulo: "PDI",
      selector: ({ pdi }) => pdi ?? SEM_VALOR,
      sortable: true,
      sortField: "pdi",
      minWidth: "150px",
    },
    {
      chave: "empresa",
      titulo: "Empresa",
      selector: ({ empresa }) => empresa ?? SEM_VALOR,
      sortable: true,
      sortField: "empresa",
      minWidth: "250px",
    },
    {
      chave: "processo",
      titulo: "Processo",
      selector: ({ idProcesso }) => idProcesso ?? SEM_VALOR,
      cell: ({ idProcesso }) =>
        idProcesso ? (
          <LinkProcesso
            type="button"
            onClick={() => redirecionarProcesso(idProcesso)}
          >
            {idProcesso}
          </LinkProcesso>
        ) : (
          SEM_VALOR
        ),
      sortable: true,
      sortField: "idProcesso",
      width: "120px",
    },
    {
      chave: "placa",
      titulo: "Placa",
      selector: ({ placa }) => placa,
      sortable: true,
      sortField: "placa",
      width: "110px",
    },
    {
      chave: "renavam",
      titulo: "Renavam",
      selector: ({ renavam }) => renavam ?? SEM_VALOR,
      sortable: true,
      sortField: "renavam",
      width: "140px",
    },
    {
      chave: "chassi",
      titulo: "Chassi",
      selector: ({ chassi }) => chassi ?? SEM_VALOR,
      sortable: true,
      sortField: "chassi",
      width: "190px",
    },
    {
      chave: "marcaModelo",
      titulo: "Marca/Modelo",
      selector: ({ marcaModelo }) => marcaModelo ?? SEM_VALOR,
      sortable: true,
      sortField: "marcaModelo",
      minWidth: "220px",
    },
    {
      chave: "processoDetran",
      titulo: "Proc. Detran",
      selector: ({ processoDetran }) => processoDetran ?? SEM_VALOR,
      sortable: true,
      sortField: "processoDetran",
      width: "150px",
    },
  ];

  return (
    <>
      <LoadingScreen mostrar={enviando} zIndex={2000} />
      <ModalRetorno {...propsModalRetorno} />
      {mostrarEnvio && (
        <EnviarSolicitacaoModal
          show={mostrarEnvio}
          quantidade={selecionados.length}
          carregando={enviando}
          handleClose={() => setMostrarEnvio(false)}
          onConfirmar={confirmarEnvio}
        />
      )}
      <TabelaPadrao
        titulo="Solicitações pendentes"
        chaveConfigColunas="placaTerceiros:solicitacoes:colunas"
        limitarALargura
        colunas={colunas}
        dados={solicitacoes}
        carregando={carregando}
        totalRegistros={totalRegistros}
        limite={limite}
        colunaOrdenadaInicial={ORDENACAO_SOLICITACAO_TERCEIROS_PADRAO.campo}
        reiniciarPaginacao={reiniciarPaginacao}
        limparSelecao={limparSelecao}
        onMudarPagina={onMudarPagina}
        onMudarLimite={onMudarLimite}
        onOrdenar={onOrdenar}
        onExportar={onExportar}
        exportando={exportando}
        selecionavel
        onSelecionar={setSelecionados}
        botoesExtras={
          <>
            <BotaoMoverTipo
              idsSolicitacao={selecionados.map(({ id }) => id)}
              destino={TIPO_SOLICITACAO_PLACA.INOVA}
              onMovidas={() => {
                setLimparSelecao((valor) => valor + 1);
                onMovidas();
              }}
            />
            <PrimaryButton
              type="button"
              disabled={selecionados.length === 0}
              onClick={() => setMostrarEnvio(true)}
            >
              Enviar solicitação
            </PrimaryButton>
          </>
        }
      />
    </>
  );
}
